// Catalog data contract (mirrors the JSON the converter produces and the backend passes
// through unchanged). Kept hand-written because Phase 1 streams raw JSON rather than going
// through the typed OpenAPI/orval client.

export type Localized = Partial<Record<string, string>>;

export type ArticleType = 'product' | 'part';

/** One line of the flat index. Products carry navigation + facet attributes inline. */
export interface IndexItem {
    artNr: string;
    type: ArticleType;
    // null for an article that has no name in this language (the converter delivers it that way,
    // see its GetLocalizedOrFallback) - not '', so that a fallback like `name ?? artNr` still
    // works. Never call a string method on it directly: normalize(item.name ?? '').
    name: string | null;
    family?: boolean;
    image?: string | null;
    // Small preview variant of `image` (assets/tn/), resolved alongside it - see provider.ts's
    // resolveIndexImages. Optional even when `image` is set: the tn/ inbox stream is optional,
    // so a delivery can have original product images without dedicated thumbnails yet: ProductGrid
    // tries this first and falls back to `image` if the thumbnail 404s.
    imageThumbnailUrl?: string;
    // The bare assetId behind `image`/`imageThumbnailUrl`, kept alongside the resolved URLs (which
    // is all `image` still holds after provider.ts's resolveIndexImages ran). Delivered by
    // thumbnails.json, not by the index row itself - and searchable, so a UUID from a failing
    // request or a blob listing can be pasted straight into the search box (whole, see engine.ts's
    // ID_FIELDS).
    imageAssetId?: string;
    // System-level (leaf) tree node ids the article belongs to; may span parallel trees.
    nodeIds?: string[];
    // "global" (has an Article_central record) vs "local" (Article_local only, no global
    // counterpart) - only set for products, same as the other facet-like fields below.
    scope?: 'global' | 'local';
    // Merged in client-side from search-index.<lang>.json (see provider.ts's
    // resolveIndexDescriptions), not delivered by the lean index itself - sparse, may be absent.
    description?: string;
    // Same sparse merge as description, from search-index.<lang>.json's "intendedUse" field
    // (AT_intended_use) - kept out of the lean index for the same reason as description.
    intendedUse?: string;
    // Facetable attribute values (string | string[] | number | boolean), keyed by attribute key.
    [attribute: string]: unknown;
}

/** STIBO-derived structural tree: portfolio -> group -> family -> system. Language-neutral. */
export type TreeNodeType = 'portfolio' | 'group' | 'family' | 'system';

export interface TreeNode {
    nodeId: string;
    type: TreeNodeType;
    // Absent on an unresolved node (see `_unresolved`) - resolve it through nodeLabel, never
    // through `loc(node.name, locale, node.nodeId)`, which puts a raw UUID on screen.
    name?: Localized;
    sequence: string;
    children: TreeNode[];
    // Only present on 'system' leaves, which are the only nodes articles reference directly.
    articleNumbers?: string[];
    articleCount?: number;
    /**
     * The converter emits this when a family REFERENCES a system whose STEP object was never
     * delivered (see CatalogConverter.BuildTreeNode's Unresolved branch): the node exists in the
     * navigation, but carries no name and no articles. The DOKA delivery of 08/2026 has 31 of
     * them (21 of which do carry documents), so the UI has to stay readable in their presence.
     */
    _unresolved?: boolean;
    /** Same gap one level down: articles this system references that the delivery never shipped
     * (2087 references across 63 systems in the 08/2026 delivery). Diagnostics only - the UI shows
     * what the index actually contains; scripts/products.validate.ts reports the shortfall. */
    _unresolvedArticleRefs?: string[];
}

export type Tree = TreeNode[];

export type AttributeValueType = 'simple' | 'localized' | 'lov' | 'measurement';

export interface AttributeMappingEntry {
    facetKey?: string;
    label: Localized;
    group: Localized;
    valueType: AttributeValueType;
    // Display order, both driven by the customer's attribute categorization spreadsheet
    // ("Datenkategorisierung", Kategorie V1/Sort. columns). groupOrder ranks the groups
    // themselves (same value on every entry sharing a group; gaps are fine, only relative order
    // matters); order ranks attributes within their group. Both optional - entries without them
    // sort after all ranked ones, by label, so an incompletely configured mapping degrades
    // gracefully instead of crashing.
    groupOrder?: number;
    order?: number;
}

/** Presentation metadata for raw STIBO attribute ids, keyed by attribute id (e.g. "AT_net_weight"). */
export type AttributeMapping = Record<string, AttributeMappingEntry>;

/**
 * Per-language attribute/LOV label table (labels.<lang>.json, from pih-stibo-converter's
 * configured data model) - translates raw attribute ids and LOV codes to display text.
 * `lovs[attrId]` is only present for LOV-bound attributes; a missing entry (or a missing code
 * within it) means no translation is known, callers fall back to the raw code.
 */
export interface CatalogLabels {
    language: string;
    attributes: Record<string, string>;
    lovs: Record<string, Record<string, string>>;
}

/** A document assigned to the article (or inherited from one of its tree systems) - resolved from STIBO's Document_central/Document_language_variant_central via referencedBy.REF_document_assignement. */
export interface MediaItem {
    assetId: string;
    mimeType?: string;
    descr?: string;
    purpose?: string | null;
    // Parsed from the variant's AT_document_identification suffix (e.g. "DO_..._bg" -> "bg") -
    // STIBO's raw meta-envelope carries no dedicated language attribute for document variants.
    language?: string | null;
    // Document_central.metadata.parentId, only when it is a symbolic STIBO taxonomy node (e.g.
    // "PPH_product_data_sheet") rather than a plain UUID parent reference.
    docType?: string | null;
    // AT_edition_date on the language variant, delivered ready-to-display as "MM/YYYY" (e.g.
    // "10/2018") - shown as-is, no reparsing.
    editionDate?: string | null;
    // Document_central's own stepId - groups language variants of the SAME logical document
    // (several MediaItem/DocumentIndexItem rows can share one documentId, one per language) so
    // the UI can collapse them into a single row with a language switcher instead of one row per
    // language (see engine.groupMediaByDocument). Falls back to this row's own assetId on the
    // rare path with no resolvable Document_central - still a valid, single-member group key.
    documentId?: string;
    url?: string;
}

/**
 * A row of the catalog-wide document index (documents.json) - same fields as MediaItem plus the
 * targets the file is assigned to, since this is not scoped to one article's detail page.
 *
 * ONE ROW PER assetId. A document assigned to many articles appears once and carries all of them
 * in `artNrs`, rather than once per article. That matters at DOKA's scale: documents are inherited
 * down from system nodes, so a single PDF can hang on dozens of articles - the delivered sample
 * has one asset on 91. Flat, that shape extrapolates to ~429,000 rows at 100,000 articles; grouped
 * it stays in the low thousands. See docs/09-ladezeit-messungen.md for the measurements.
 *
 * Both lists can be set at once: the same file may be assigned directly to a tree system node AND
 * to individual articles. `nodeIds` holds the DIRECT system-node targets only - not the nodes
 * reachable through `artNrs`, which resolve through the index (see engine.inDocumentCategory) -
 * and each node appears at most once even when several families reference the same system.
 */
export interface DocumentIndexItem extends MediaItem {
    artNrs?: string[];
    nodeIds?: string[];
}

export interface ArticleDetail {
    artNr: string;
    type: ArticleType;
    name: Localized;
    nodeIds: string[];
    // Raw STIBO attribute values, keyed by attribute id; shape per AttributeMapping[id].valueType.
    attributes: Record<string, unknown>;
    media: MediaItem[];
    // Product photo (assetId, resolved to a URL by the provider) - from Article_central's REF_Article_Image_Colour.
    image?: string | null;
    // Same photo, resolved against the thumbnail variant (assets/tn/) - fast preview; click through to `image` for the original.
    imageThumbnailUrl?: string;
    // Links to other articles, as article numbers - absent when the article has none.
    relations?: ArticleRelations;
}

/**
 * An article's links to other articles (converter: CatalogProjection.RelationIds), resolved to
 * article numbers. STIBO delivers both ends of each link, so "spare part FOR" comes from the
 * article itself, not from a catalog-wide reverse lookup. Every list is absent when empty; names
 * and images come from the index (the detail file stays independent of the other article's data).
 */
export interface ArticleRelations {
    /** REF_spare_part_article, referenced: this article's spare parts. */
    spareParts?: string[];
    /** REF_spare_part_article, referencedBy: the articles this one is a spare part for. */
    sparePartOf?: string[];
    /** REF_substitute_article, referenced. */
    substitutes?: string[];
    /** REF_substitute_article, referencedBy. */
    substituteFor?: string[];
}

/**
 * Detail document for a single System tree node (systems/<nodeId>.json) - the navigation tree's
 * leaf level. Complements the tree's own `articleNumbers`/`articleCount` (that system's article
 * list - already in tree.json) and the catalog-wide document index (filterable by `nodeId` -
 * already in documents.json): this artefact only adds what neither of those carries.
 */
export interface SystemDetail {
    nodeId: string;
    name: Localized;
    description?: Localized;
    // Same asset-resolution shape as ArticleDetail.image/imageThumbnailUrl.
    image?: string | null;
    imageThumbnailUrl?: string;
    lifecycleGroup?: string;
    subsidiary?: string;
    archived?: boolean;
    // Documents assigned to this system node itself. They used to be copied into the `media[]` of
    // every article beneath it - measured, 94% of all media entries in the catalog were such
    // copies (the system "DokaTruss-Tisch" alone has 299 articles, so its 8 documents were stored
    // 299 times). They live here once now, and an article page pulls them in through its nodeIds.
    media?: MediaItem[];
}

export type FacetType = 'value-list' | 'range' | 'boolean' | 'date-range';

export interface FacetDef {
    key: string;
    label: Localized;
    type: FacetType;
    selection: 'single' | 'multi';
    // Rendering hint per type ("checkbox", "switch", "date-range", ...). For `range` facets it is
    // informational only: they always render as von/bis fields (FacetsPanel's MinMaxRow) - the
    // slider the value "range" used to select was dropped at the customer's request.
    control: string;
    unit?: string;
    // STIBO attribute id (e.g. "AT_lifecycle_group") whose LOV table in labels.<lang>.json
    // translates this facet's raw values to display text - only for value-list/boolean facets
    // backed by an LOV-bound attribute. Omitted when the facet's values are already display
    // text (e.g. a range facet, or a synthetic field with no single source attribute).
    attrId?: string;
    // Value-list facets only: order of the values in the drawer and in the landing page's
    // selects. Default is by count (most frequent first, then code); 'label' sorts by the LOV
    // clear text instead (customer ask for Landesgesellschaft). `firstValues` pins these codes to
    // the top in the given order regardless of sort ("Alle Länder" before the countries).
    sort?: 'count' | 'label';
    firstValues?: string[];
    // Boolean facets only: items with this field === true are excluded whenever the facet's own
    // filter isn't explicitly set (e.g. archived articles stay hidden until a user opts in via
    // the facet's switch) - see engine.ts's applyFilters/computeFacets.
    hideByDefault?: boolean;
}

export interface SearchField {
    key: string;
    pattern: 'contains' | 'prefix' | 'exact';
    weight: number;
}

export interface SearchConfig {
    fields: SearchField[];
    minChars: number;
    includeParts: boolean;
}

/**
 * A named, pre-configured filter set (deep link target, `/catalog?preset=<key>`) - see
 * CatalogPage's preset handling. Its `filters` are applied ONCE on arrival as ordinary, fully
 * editable filters; they are not locked or hidden (they used to be, see chat: the customer wants
 * the Landesgesellschaft/Lifecycle choice visible and changeable in the results list). The landing
 * page's "Weltweites Sortiment" checkbox uses the same preset as its filter source (SearchMask).
 */
export interface CatalogPreset {
    key: string;
    label: Localized;
    filters: ActiveFilters;
}

export interface CatalogConfig {
    facets: FacetDef[];
    search: SearchConfig;
    presets?: CatalogPreset[];
}

export interface DetailLayoutTab {
    id: string;
    label: Localized;
    source: 'base' | 'attributes' | string;
}

export interface DetailLayout {
    tabs: DetailLayoutTab[];
}

// ---- client-side filter state -------------------------------------------------------

export interface RangeValue {
    min?: number;
    max?: number;
}

/** von/bis for a 'date-range' facet - ISO date strings ("YYYY-MM-DD"), compared lexicographically
 * (which sorts correctly for that format, no Date parsing needed in the pure engine). */
export interface DateRangeValue {
    from?: string;
    to?: string;
}

export type FilterValue = string[] | RangeValue | DateRangeValue | boolean;

export type ActiveFilters = Record<string, FilterValue>;

// ---- computed facet output (for rendering) ------------------------------------------

export interface FacetValueCount {
    value: string;
    count: number;
}

export interface ComputedFacet extends FacetDef {
    values?: FacetValueCount[]; // value-list
    bounds?: { min: number; max: number }; // range
    counts?: { true: number; false: number }; // boolean
    dateBounds?: { min: string; max: string }; // date-range
}

/** A selectable extra column in the list view. */
export interface ListColumn {
    key: string;
    label: string;
    unit?: string;
}

// All `||`, not `??`: an existing-but-empty string (e.g. a cleared translation) must fall through
// to the next language too, not stop the chain and lose a perfectly good value in another one.
export const loc = (map: Localized | undefined, lang: string, fallback = ''): string =>
    (map && (map[lang] || map.de || map.en)) || fallback;

/** Placeholder for a tree node the delivery never named (see TreeNode._unresolved). */
const UNNAMED_NODE: Localized = { de: 'Ohne Bezeichnung', en: 'Unnamed' };

/**
 * Display label of a tree node - its localized name, or a placeholder plus the short id when the
 * delivery carries no name for it at all.
 *
 * Every navigation label, breadcrumb, "Verwendung" entry and system title goes through here. The
 * previous fallback was the node's own UUID, which is what put entries like
 * "53ece223-8cf7-425c-9dbf-77b78cf1c65e" in the category tree (see chat screenshot). The short id
 * stays in the label on purpose: several unnamed nodes can sit under the same parent (9 under
 * "Schalungsplatten" in the 08/2026 delivery), and it is what a data question to DOKA needs.
 */
export const nodeLabel = (node: Pick<TreeNode, 'nodeId' | 'name'> | undefined, lang: string): string => {
    if (!node) {
        return '';
    }
    return loc(node.name, lang, `${loc(UNNAMED_NODE, lang, 'Unnamed')} (${node.nodeId.slice(0, 8)})`);
};
